<template>
  <div v-if="isConsumerDataset && producer_object">
    <a @click="$emit('selectTreeItem', buildSelectedTreeItem(producer_object))"
      class="link">
      <span>{{ `${producer_object.schema}.${producer_object.object} (${producer_object.account})` }}</span>&nbsp;
    </a>
    <Tooltip direction="top" contents="Copy link" class="inline">
      <a>
        <i v-clipboard:copy="getObjectName(producer_object.account, producer_object.database, producer_object.schema, producer_object.object)"
          class="fa fa-copy customlink" />
      </a>
    </Tooltip>
  </div>

  <div v-else-if="connectedDatasets.length">
    <div class="accordion">
      <div class="accordion-header" @click="baseAccordianExpanded = !baseAccordianExpanded">
        <div class="accordion-arrow">
          <i class="fa fa-lg" :class="{ 'fa-angle-down': baseAccordianExpanded, 'fa-angle-right': !baseAccordianExpanded }" />
        </div>
        <TableLayout class="layout-icon" />
        <span class="object-name" v-if="connectedDatasets[0].baseObject.isRequester">{{ connectedDatasets[0].baseObject.object }}</span>
        <a v-else
          href="#"
          @click.stop.prevent="$emit('selectTreeItem', buildSelectedTreeItem(connectedDatasets[0].baseObject))"
          class="link">
          {{ connectedDatasets[0].baseObject.object }}
        </a>
      </div>
      <transition name="accordion-smooth">
        <div v-if="baseAccordianExpanded" class="accordion-body">
          <div>
            <Accordion v-if="directShares.length" :title="`Direct Shares (${directShares.length})`" variant="minimal"
              defaultOpen=true :headerStyle="{ padding: 0, backgroundColor: '#fff', color: '#3A414C' }"
              :bodyStyle="{ paddingBottom: 0 }">
              <div class="direct-shares-list">
                <template v-for="share in directShares">
                  <template v-for="consumer in share.consumers">
                    <span class="chip" @click="$emit('selectTreeItem', buildSelectedTreeItem(consumer))">
                      {{ consumer.account }}
                    </span>
                  </template>
                </template>
              </div>
            </Accordion>
            <Accordion v-if="secureViews.length" :title="`Secure Views (${secureViews.length})`" variant="minimal"
              defaultOpen=true :headerStyle="{ padding: 0, backgroundColor: '#fff', color: '#3A414C' }"
              :bodyStyle="{ paddingBottom: 0 }">
              <template v-for="share in secureViews">
                <div class="secure-view-row">
                  <div class="secure-view-header">
                    <IconLayers class="secure-view-icon" />
                    <a v-if="share.secureView.isRequester != true"
                      @click="$emit('selectTreeItem', buildSelectedTreeItem(share.secureView))"
                      class="link">
                      <span>{{ share.secureView.object }}</span>&nbsp;
                    </a>
                    <span v-else class="secure-view-object"><span>{{ share.secureView.object }}</span>&nbsp;</span>
                  </div>
                  <div class="secure-view-consumers">
                    <template v-for="consumer in share.consumers">
                      <span class="chip" @click="$emit('selectTreeItem', buildSelectedTreeItem(consumer))">
                        {{ consumer.account }}
                      </span>
                    </template>
                  </div>
                </div>
              </template>
            </Accordion>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import { Tooltip, Accordion } from '@cimpress/react-components';
import { IconLayers } from '@cimpress-technology/react-streamline-icons/lib';
import TableLayout from '../../Icons/TableLayout.vue';

export default {
  name: 'DataSharesList',
  components: {
    Tooltip,
    Accordion,
    IconLayers,
    TableLayout
  },
  props: {
    connectedDatasets: { type: Array, required: true },
  },
  data() {
    return {
      baseAccordianExpanded: true
    };
  },
  computed: {
    isConsumerDataset() {
      return (
        this.connectedDatasets.length &&
        Array.isArray(this.connectedDatasets[0].consumers) &&
        this.connectedDatasets[0].consumers.some(consumer => consumer.isRequester)
      );
    },
    producer_object() {
      if (!this.connectedDatasets.length) return undefined;
      const producer = this.connectedDatasets[0];
      return producer.secureView || producer.baseObject || undefined;
    },
    directShares() {
      return this.connectedDatasets.filter(s => s.kind === 'TABLE');
    },
    secureViews() {
      return this.connectedDatasets.filter(s => s.kind === 'SECURE VIEW');
    }
  },
  methods: {
    buildSelectedTreeItem(obj) {
      return `${obj.account.toLowerCase()}.eu-west-1.${obj.database}.${obj.schema.toLowerCase()}.${obj.object.toLowerCase()}`;
    },
    getObjectName(account, database, schema, object) {
      return `${account}.${database}.${schema}.${object}`;
    }
  }
};
</script>
<style scoped>
.layout-icon {
  width: 12px;
  vertical-align: baseline;
  margin-right: 8px;
}

.accordion-body .link {
  color: #0088A9;
  text-decoration: none;
  cursor: pointer;
}

.accordion .object-name {
  font-weight: 900;
}

.accordion .chip {
  font-size: 12px;
  cursor: pointer;
  background: #EFF3F5;
  color: #3A414C;
  border-radius: 23px;
  padding: 5px 9px;
  margin-right: 6px;
  font-weight: 600;
  transition: background 0.2s, color 0.2s;
}

.accordion .chip:hover {
  color: #fff;
  background: #00738E;
}

.accordion .accordion-body {
  padding-top: 0px;
  padding-bottom: 0px;
  padding-left: 25px;
  overflow: hidden;
  min-height: 0;
  transition: padding 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.accordion .accordion-header {
  background-color: #fff;
  padding-top: 0px;
  padding-left: 0px;
  padding-bottom: 5px;
}

.accordion {
  border: none;
}

.direct-shares-list {
  display: flex;
  margin-left: 15px;
  flex-wrap: wrap;
  row-gap: 8px;
}

.secure-view-row {
  margin-bottom: 8px;
}

.secure-view-header {
  display: flex;
}

.secure-view-icon {
  color: #8090A2;
  margin-right: 10px;
  display: flex;
  align-items: center;
}

.secure-view-object {
  font-weight: 900;
}

.secure-view-consumers {
  margin-left: 24px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 4px;
}

.accordion-smooth-enter-active,
.accordion-smooth-leave-active {
  transition: max-height 1s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s;
}
.accordion-smooth-enter,
.accordion-smooth-leave-to {
  max-height: 0 !important;
  opacity: 0;
}
.accordion-smooth-enter-to,
.accordion-smooth-leave {
  max-height: 1000px !important;
  opacity: 1;
}
</style>
